import { useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface PreviewWithDataDialogProps {
  open: boolean;
  html: string;
  css: string;
  onOpenChange: (open: boolean) => void;
}

const VAR_PATTERN = /\{\{\s*(\w+)\s*\}\}/g;

function detectVariables(html: string): string[] {
  const set = new Set<string>();
  for (const match of html.matchAll(VAR_PATTERN)) {
    set.add(match[1]);
  }
  return Array.from(set).sort();
}

function substitute(html: string, values: Record<string, string>): string {
  return html.replace(VAR_PATTERN, (_, key: string) =>
    values[key] !== undefined && values[key] !== ""
      ? values[key]
      : `{{${key}}}`,
  );
}

function wrapDoc(html: string, css: string): string {
  return `<!doctype html><html><head><meta charset="utf-8"/><style>${css}</style></head><body>${html}</body></html>`;
}

export function PreviewWithDataDialog({
  open,
  html,
  css,
  onOpenChange,
}: PreviewWithDataDialogProps) {
  const variables = useMemo(() => detectVariables(html), [html]);
  const [values, setValues] = useState<Record<string, string>>({});

  const srcDoc = useMemo(
    () => wrapDoc(substitute(html, values), css),
    [html, css, values],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl">
        <DialogHeader>
          <DialogTitle>Preview with sample data</DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-[260px_1fr] gap-4 h-[60vh]">
          <div className="border rounded-md p-3 overflow-y-auto">
            <div className="text-xs font-medium text-muted-foreground mb-2">
              Detected variables
            </div>
            {variables.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No <code>{`{{variables}}`}</code> found in this template.
              </p>
            ) : (
              <div className="space-y-3">
                {variables.map((name) => (
                  <div key={name} className="space-y-1">
                    <Label htmlFor={`var-${name}`} className="text-xs">
                      {name}
                    </Label>
                    <Input
                      id={`var-${name}`}
                      value={values[name] ?? ""}
                      placeholder={`{{${name}}}`}
                      onChange={(e) =>
                        setValues((prev) => ({
                          ...prev,
                          [name]: e.target.value,
                        }))
                      }
                    />
                  </div>
                ))}
              </div>
            )}
          </div>

          <iframe
            title="Email preview"
            srcDoc={srcDoc}
            className="w-full h-full border rounded-md bg-white"
            sandbox=""
          />
        </div>
      </DialogContent>
    </Dialog>
  );
}
